import { Box, Button, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import { useBoardEditingStore, useWorkspaceUiStore, type WallBoard } from '@walls/store';
import { useCardCommands } from '../cards/actions/useCardCommands';
import { CardActionPanel } from './CardActionPanel';
import { CardColorPicker } from './CardColorPicker';
import { useFocusTextFieldAtEnd } from './hooks/useFocusTextFieldAtEnd';
import { RichTextCardEditor } from './RichTextCardEditor';
import { boardCardColors } from './utils/cardColors';
import { sanitizeEditorRichText, stripHtml, toRichTextHtml } from './utils/richText';

interface AddTextCardDialogProps {
  board: WallBoard;
}

export function AddTextCardDialog({ board }: AddTextCardDialogProps) {
  const createCardInitialText = useBoardEditingStore((state) => state.createCardInitialText);
  const createCardTargetClusterId = useBoardEditingStore(
    (state) => state.createCardTargetClusterId
  );
  const textField = useFocusTextFieldAtEnd([createCardInitialText]);
  const cardCommands = useCardCommands(board);
  const stopEditing = useBoardEditingStore((state) => state.stopEditing);
  const selectedCardColor = useWorkspaceUiStore((state) => state.selectedCardColor);
  const selectedCardShape = useWorkspaceUiStore((state) => state.selectedCardShape);
  const setSelectedCardColor = useWorkspaceUiStore((state) => state.setSelectedCardColor);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [color, setColor] = useState(selectedCardColor);
  const [content, setContent] = useState(createCardInitialText);
  const [richEditing, setRichEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    if (!isSaving) {
      stopEditing();
    }
  };

  const save = async () => {
    const trimmedContent = richEditing ? sanitizeEditorRichText(content) : content.trim();

    if (!stripHtml(trimmedContent) || isSaving) {
      close();
      return;
    }

    setIsSaving(true);

    try {
      await cardCommands.createTextCard(
        selectedCardShape,
        trimmedContent,
        color,
        createCardTargetClusterId
      );
      stopEditing();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardActionPanel
      backgroundColor={color}
      onClose={close}
      onClick={() => setShowColorPicker(false)}
    >
      {richEditing ? (
        <RichTextCardEditor initialValue={content} onChange={setContent} />
      ) : (
        <TextField
          inputRef={textField.inputRef}
          multiline
          rows={5}
          value={content}
          onFocus={textField.onFocus}
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void save();
            }
          }}
          sx={{
            height: 260,
            '& .MuiOutlinedInput-root': {
              alignItems: 'flex-start',
              height: '100%',
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },

              '& .MuiInputBase-input': {
                boxSizing: 'border-box',
                fontSize: '36px',
                height: '100% !important',
                lineHeight: 1.2,
                overflowY: 'auto !important',
                resize: 'none',
              },
            },
          }}
        />
      )}
      <Stack
        direction="row"
        justifyContent={!richEditing ? 'space-between' : 'flex-end'}
        alignItems="end"
        spacing={2}
      >
        {!richEditing && (
          <Button
            variant="text"
            onClick={() => {
              setContent(toRichTextHtml(content));
              setRichEditing(true);
            }}
          >
            More
          </Button>
        )}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
            onClick={(event) => event.stopPropagation()}
          >
            <Box
              component="button"
              type="button"
              aria-label={`Use color ${color}`}
              onClick={() => setShowColorPicker(!showColorPicker)}
              sx={{
                bgcolor: color,
                border: '1px solid #d0d5dd',
                borderRadius: '50%',
                boxShadow: color === '#ffffff' ? 'inset 0 0 0 1px #e4e7ec' : undefined,
                cursor: 'pointer',
                height: 30,
                width: 30,
                p: 0,
              }}
            />
            {showColorPicker && (
              <CardColorPicker
                colors={boardCardColors}
                value={color}
                onChange={(nextColor) => {
                  setShowColorPicker(false);
                  setColor(nextColor);
                  setSelectedCardColor(nextColor);
                }}
              />
            )}
          </Box>
          <Button variant="contained" onClick={() => void save()} disabled={isSaving}>
            Post
          </Button>
        </Stack>
      </Stack>
    </CardActionPanel>
  );
}
